/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { databaseService } from '../database/database.service.js';

/**
 * Group Repository
 * Handles all database operations for groups
 */
class GroupRepository {
  /**
   * Get all groups from the database
   * @returns {Promise<Array<import('../../models/group.model.js').Group>>}
   */
  async findAll() {
    try {
      const result = await databaseService.query('SELECT * FROM groups ORDER BY created_at DESC');
      
      return this.mapRows(result.values || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw new Error('Failed to fetch groups from database');
    }
  }

  /**
   * Get a single group by ID
   * @param {number} id - The group's ID
   * @returns {Promise<import('../../models/group.model.js').Group|null>}
   */
  async findById(id) {
    try {
      const result = await databaseService.query('SELECT * FROM groups WHERE id = ?', [id]);
      
      const rows = this.mapRows(result.values || []);
      return rows.length > 0 ? rows[0] : null;
    } catch (error) {
      console.error('Error fetching group by ID:', error);
      throw new Error('Failed to fetch group from database');
    }
  }

  /**
   * Create a new group
   * @param {Omit<import('../../models/group.model.js').Group, 'id'|'createdAt'|'updatedAt'|'goatCount'>} group
   * @returns {Promise<number>} The ID of the created group
   */
  async create(group) {
    try {
      const query = `
        INSERT INTO groups (name, location, description)
        VALUES (?, ?, ?)
      `;
      
      const result = await databaseService.run(query, [
        group.name,
        group.location || null,
        group.description || null
      ]);

      return result.changes?.lastId || 0;
    } catch (error) {
      console.error('Error creating group:', error);
      throw new Error('Failed to create group in database');
    }
  }

  /**
   * Update an existing group
   * @param {number} id - The group's ID
   * @param {Partial<import('../../models/group.model.js').Group>} group - Group data to update
   * @returns {Promise<void>}
   */
  async update(id, group) {
    try {
      const query = `
        UPDATE groups 
        SET name = ?, location = ?, description = ?, updated_at = CURRENT_TIMESTAMP
        WHERE id = ?
      `;
      
      await databaseService.run(query, [
        group.name,
        group.location || null,
        group.description || null,
        id
      ]);
    } catch (error) {
      console.error('Error updating group:', error);
      throw new Error('Failed to update group in database');
    }
  }

  /**
   * Delete a group
   * IMPORTANT: This will unassign all goats from this group (set group_id to NULL)
   * @param {number} id - The group's ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      // STEP 1: Unassign all goats from this group
      // This is necessary for IndexedDB/Dexie which doesn't have foreign key constraints
      // For SQLite, the ON DELETE SET NULL constraint would handle this, but we do it manually for consistency
      await databaseService.run('UPDATE goats SET group_id = NULL WHERE group_id = ?', [id]);
      
      console.log(`Unassigned all goats from group ${id}`);
      
      // STEP 2: Delete the group
      await databaseService.run('DELETE FROM groups WHERE id = ?', [id]);
      
      console.log(`Deleted group ${id}`);
    } catch (error) {
      console.error('Error deleting group:', error);
      throw new Error('Failed to delete group from database');
    }
  }

  /**
   * Map database rows to group objects
   * @private
   * @param {Array<any>} rows - Database rows
   * @returns {Array<import('../../models/group.model.js').Group>}
   */
  mapRows(rows) {
    return rows.map(row => ({
      id: row.id,
      name: row.name,
      location: row.location,
      description: row.description,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  }
}

// Export singleton instance
export const groupRepository = new GroupRepository();
